import { useState, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../../lib/design';
import { Ic } from '../../lib/ui';
import { useI18n } from '../../i18n';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

const ACCENT = '#0E243E';

const TECH_ICONS: Record<string, ReactNode> = {
  apps: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  proyectos: (
    <>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </>
  ),
  compras: (
    <>
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </>
  ),
  activos: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </>
  ),
  peticiones: (
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  ocr: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="7" y1="8" x2="17" y2="8" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="7" y1="16" x2="13" y2="16" />
    </>
  ),
};

export function TechPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const tools = useMemo(
    () => [
      {
        label: t.tech.tabs.apps,
        desc: t.tech.descriptions.apps,
        icon: TECH_ICONS.apps,
        path: '/tech/apps',
      },
      {
        label: t.tech.tabs.projects,
        desc: t.tech.descriptions.projects,
        icon: TECH_ICONS.proyectos,
        path: '/tech/proyectos',
      },
      {
        label: t.tech.tabs.procurement,
        desc: t.tech.descriptions.procurement,
        icon: TECH_ICONS.compras,
        path: '/sign/compras',
      },
      {
        label: t.tech.tabs.assets,
        desc: t.tech.descriptions.assets,
        icon: TECH_ICONS.activos,
        path: '/tech/activos',
      },
      {
        label: t.tech.tabs.requests,
        desc: t.tech.descriptions.requests,
        icon: TECH_ICONS.peticiones,
        path: '/requests/admin',
      },
      {
        label: t.tech?.tabs?.ocr ?? 'OCR / Digitalizacion',
        desc: t.tech?.descriptions?.ocr ?? 'Escanear documentos fisicos y PDFs para digitalizar',
        icon: TECH_ICONS.ocr,
        path: '/tech/ocr',
      },
    ],
    [t],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F8F8F8',
          padding: 48,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 800,
            gap: 24,
          }}
        >
          {/* 390.M5 — título con InfoTooltip anclado (antes position:absolute top:68px). */}
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <h1
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#1F3864',
                margin: 0,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {t.sidebar.pageTitle.tech}
            </h1>
            <InfoTooltip
              title={t.sidebar.pageTitle.tech}
              text={t.disciplines.tooltips.tech}
              position="right"
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 240px))',
              gap: 24,
              justifyContent: 'center',
              maxWidth: 800,
            }}
          >
            {tools.map((tool, idx) => (
              <div
                key={tool.path}
                onClick={() => navigate(tool.path)}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  borderTop: `4px solid ${ACCENT}`,
                  overflow: 'hidden',
                  padding: '28px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  boxShadow: hoveredIdx === idx ? C.shadowHover : C.shadow1,
                  transform: hoveredIdx === idx ? 'translateY(-4px)' : 'none',
                  transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: `${ACCENT}0A`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Ic d={tool.icon} s={28} c={ACCENT} />
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{tool.label}</div>
                <div
                  style={{ fontSize: 13, color: 'rgba(0,0,0,0.54)', marginTop: 6, lineHeight: 1.5 }}
                >
                  {tool.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
